// Marketing activity, products / prices, trends and the competitor search.
import type { Identifier } from "ra-core";

import type { MarketCompetitor } from "@/components/market-intel/types";

import {
  foldText,
  isInvalidAmount,
  optionalText,
  optionalUrl,
  parseAmount,
  parseOptionalDay,
  type FormResult,
} from "./forms";
import {
  isPlatform,
  matchesPlatformFilter,
  type PlatformFilter,
} from "./platforms";
import type {
  ActivityKind,
  BillingPeriod,
  CompetitorMarketingActivity,
  CompetitorProduct,
  MarketTrend,
  SocialPlatform,
  TrendRelevance,
} from "./types";

export const ACTIVITY_KINDS: readonly ActivityKind[] = [
  "post",
  "video",
  "anuncio",
  "campana",
  "otro",
];
export const BILLING_PERIODS: readonly BillingPeriod[] = [
  "mes",
  "año",
  "pago_unico",
  "otro",
];
export const RELEVANCES: readonly TrendRelevance[] = ["alta", "media", "baja"];

export const isActivityKind = (value: unknown): value is ActivityKind =>
  ACTIVITY_KINDS.includes(value as ActivityKind);
export const isBillingPeriod = (value: unknown): value is BillingPeriod =>
  BILLING_PERIODS.includes(value as BillingPeriod);
export const isRelevance = (value: unknown): value is TrendRelevance =>
  RELEVANCES.includes(value as TrendRelevance);

export const activityKindLabelKey = (kind: ActivityKind): string =>
  `competition.activity.kinds.${isActivityKind(kind) ? kind : "otro"}`;
export const billingPeriodLabelKey = (period: BillingPeriod): string =>
  `competition.products.periods.${period === "año" ? "ano" : period}`;
export const relevanceLabelKey = (relevance: TrendRelevance): string =>
  `competition.trends.relevance.${isRelevance(relevance) ? relevance : "media"}`;

// ---------- competitor list ----------

/** Competitors whose name, location, web or description contains `query`. */
export const searchCompetitors = (
  competitors: readonly MarketCompetitor[],
  query: string,
): MarketCompetitor[] => {
  const needle = foldText(query.trim());
  if (!needle) return [...competitors];
  return competitors.filter((competitor) =>
    [
      competitor.name,
      competitor.location,
      competitor.website,
      competitor.description,
      competitor.value_proposition,
    ].some((field) => foldText(field).includes(needle)),
  );
};

/** First letter of the name, for the avatar without a logo. */
export const initialOf = (name: string): string =>
  (name.trim().match(/\p{L}|\p{N}/u)?.[0] ?? "?").toUpperCase();

// ---------- marketing activity ----------

/** Newest first; undated items last; same day: the latest inserted first. */
export const sortActivity = (
  items: readonly CompetitorMarketingActivity[],
): CompetitorMarketingActivity[] =>
  [...items].sort(
    (a, b) =>
      (b.published_at ?? "").localeCompare(a.published_at ?? "") ||
      Number(b.id) - Number(a.id),
  );

export const filterActivity = (
  items: readonly CompetitorMarketingActivity[],
  filter: PlatformFilter,
): CompetitorMarketingActivity[] =>
  sortActivity(items).filter((item) =>
    matchesPlatformFilter(item.platform, filter),
  );

/** Most recent published_at per competitor id. */
export const latestActivityByCompetitor = (
  items: readonly CompetitorMarketingActivity[],
): Map<string, string> => {
  const latest = new Map<string, string>();
  for (const item of items) {
    if (!item.published_at) continue;
    const key = String(item.competitor_id);
    const current = latest.get(key);
    if (!current || item.published_at > current)
      latest.set(key, item.published_at);
  }
  return latest;
};

export interface ActivityFormValues {
  platform: SocialPlatform;
  kind: ActivityKind;
  title: string;
  url: string;
  published_at: string;
  description: string;
  notes: string;
}

export type ActivityPayload = Pick<
  CompetitorMarketingActivity,
  | "competitor_id"
  | "platform"
  | "kind"
  | "title"
  | "url"
  | "published_at"
  | "description"
  | "notes"
>;

export const activityFormDefaults = (
  today: string,
  item?: Partial<CompetitorMarketingActivity>,
): ActivityFormValues => ({
  platform: isPlatform(item?.platform) ? item.platform : "instagram",
  kind: isActivityKind(item?.kind) ? item.kind : "post",
  title: item?.title ?? "",
  url: item?.url ?? "",
  published_at: item ? (item.published_at ?? "") : today,
  description: item?.description ?? "",
  notes: item?.notes ?? "",
});

const MAX_TITLE = 200;

export const normalizeActivityForm = (
  competitorId: Identifier,
  values: ActivityFormValues,
): FormResult<ActivityPayload> => {
  const title = values.title.trim();
  if (!title) return { ok: false, error: "competition.form.title_required" };
  if (title.length > MAX_TITLE)
    return { ok: false, error: "competition.form.title_too_long" };
  const day = parseOptionalDay(values.published_at);
  if (!day.ok) return { ok: false, error: "competition.form.date_invalid" };
  return {
    ok: true,
    data: {
      competitor_id: competitorId,
      platform: isPlatform(values.platform) ? values.platform : "otro",
      kind: isActivityKind(values.kind) ? values.kind : "post",
      title,
      url: optionalUrl(values.url),
      published_at: day.day,
      description: optionalText(values.description),
      notes: optionalText(values.notes),
    },
  };
};

// ---------- products ----------

/** Priced plans first, cheapest first; then the rest by name. */
export const sortProducts = (
  products: readonly CompetitorProduct[],
): CompetitorProduct[] =>
  [...products].sort((a, b) => {
    const left = a.price ?? Number.POSITIVE_INFINITY;
    const right = b.price ?? Number.POSITIVE_INFINITY;
    return left === right ? a.name.localeCompare(b.name) : left - right;
  });

export interface ProductFormValues {
  name: string;
  price: string;
  currency: string;
  billing_period: BillingPeriod | "";
  description: string;
  url: string;
}

export type ProductPayload = Pick<
  CompetitorProduct,
  | "competitor_id"
  | "name"
  | "price"
  | "currency"
  | "billing_period"
  | "description"
  | "url"
>;

export const productFormDefaults = (
  product?: Partial<CompetitorProduct>,
  toInput: (value: number | null | undefined) => string = (value) =>
    value == null ? "" : String(value),
): ProductFormValues => ({
  name: product?.name ?? "",
  price: toInput(product?.price),
  currency: product?.currency ?? "EUR",
  billing_period: isBillingPeriod(product?.billing_period)
    ? product.billing_period
    : product
      ? ""
      : "mes",
  description: product?.description ?? "",
  url: product?.url ?? "",
});

export const normalizeProductForm = (
  competitorId: Identifier,
  values: ProductFormValues,
): FormResult<ProductPayload> => {
  const name = values.name.trim();
  if (!name)
    return { ok: false, error: "competition.products.form.name_required" };
  if (name.length > MAX_TITLE)
    return { ok: false, error: "competition.form.title_too_long" };
  const price = parseAmount(values.price);
  if (isInvalidAmount(price))
    return { ok: false, error: "competition.products.form.price_invalid" };
  const currency = values.currency.trim().toUpperCase() || "EUR";
  if (!/^[A-Z]{3}$/.test(currency))
    return { ok: false, error: "competition.products.form.currency_invalid" };
  return {
    ok: true,
    data: {
      competitor_id: competitorId,
      name,
      price,
      currency,
      billing_period: isBillingPeriod(values.billing_period)
        ? values.billing_period
        : null,
      description: optionalText(values.description),
      url: optionalUrl(values.url),
    },
  };
};

// ---------- trends ----------

export type RelevanceFilter = TrendRelevance | "all";
export const RELEVANCE_FILTERS: readonly RelevanceFilter[] = [
  "all",
  ...RELEVANCES,
];

/** Newest first; same day: the most relevant first. */
export const sortTrends = (trends: readonly MarketTrend[]): MarketTrend[] =>
  [...trends].sort(
    (a, b) =>
      b.detected_at.localeCompare(a.detected_at) ||
      RELEVANCES.indexOf(a.relevance) - RELEVANCES.indexOf(b.relevance) ||
      Number(b.id) - Number(a.id),
  );

export const filterTrends = (
  trends: readonly MarketTrend[],
  platform: PlatformFilter,
  relevance: RelevanceFilter,
): MarketTrend[] =>
  sortTrends(trends).filter(
    (trend) =>
      matchesPlatformFilter(trend.platform, platform) &&
      (relevance === "all" || trend.relevance === relevance),
  );

export interface TrendFormValues {
  platform: SocialPlatform;
  title: string;
  description: string;
  url: string;
  detected_at: string;
  relevance: TrendRelevance;
  /** "" = no competitor. */
  competitor_id: string;
}

export type TrendPayload = Pick<
  MarketTrend,
  | "platform"
  | "title"
  | "description"
  | "url"
  | "detected_at"
  | "relevance"
  | "competitor_id"
>;

export const trendFormDefaults = (
  today: string,
  trend?: Partial<MarketTrend>,
  competitorId?: Identifier | null,
): TrendFormValues => ({
  platform: isPlatform(trend?.platform) ? trend.platform : "instagram",
  title: trend?.title ?? "",
  description: trend?.description ?? "",
  url: trend?.url ?? "",
  detected_at: trend?.detected_at ?? today,
  relevance: isRelevance(trend?.relevance) ? trend.relevance : "media",
  competitor_id: String(trend?.competitor_id ?? competitorId ?? ""),
});

export const normalizeTrendForm = (
  values: TrendFormValues,
  competitorIds: readonly Identifier[],
): FormResult<TrendPayload> => {
  const title = values.title.trim();
  if (!title) return { ok: false, error: "competition.form.title_required" };
  if (title.length > MAX_TITLE)
    return { ok: false, error: "competition.form.title_too_long" };
  const day = parseOptionalDay(values.detected_at);
  if (!day.ok || !day.day)
    return { ok: false, error: "competition.form.date_invalid" };
  const competitor = competitorIds.find(
    (id) => String(id) === values.competitor_id,
  );
  return {
    ok: true,
    data: {
      platform: isPlatform(values.platform) ? values.platform : "otro",
      title,
      description: optionalText(values.description),
      url: optionalUrl(values.url),
      detected_at: day.day,
      relevance: isRelevance(values.relevance) ? values.relevance : "media",
      competitor_id: competitor ?? null,
    },
  };
};
